"use client";

import React from "react";
import { ArrowRight, BriefcaseBusiness, HeartHandshake, UsersRound } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pathways = [{
  title: "Current Opportunities",
  description: "Explore open roles for people who want to support practical financial management, reporting, systems development, project work, and advisory support.",
  icon: BriefcaseBusiness,
  buttonText: "View Opportunities",
  link: "/career-opportunities/"
}, {
  title: "General Expression of Interest",
  description: "Tell us about your skills, experience, and the kind of work that interests you. We are often looking for the right person before a specific role is finalized.",
  icon: UsersRound,
  buttonText: "Send Your Interest",
  link: "/general-expression-of-interest/"
}];
export default function ReadyToGrow() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="141715" id="meaningful-work-with-riverbend" className="bg-gradient-to-b from-primary-50/60 to-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.10),transparent_38%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(17,47,75,0.08),transparent_42%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto text-center mb-14 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-secondary font-default font-bold tracking-widest uppercase text-sm mb-4">
            Work with Purpose
          </p>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-500 mb-6 leading-tight">
            Help communities and organizations move important work forward.
          </h2>

          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            RiverBend works alongside First Nation communities, Northern
            organizations, businesses, and partnerships to support financial
            management, reporting, project delivery, systems development, and
            economic development opportunities.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12 items-stretch">
          <div className={`lg:col-span-1 transition-all duration-700 delay-100 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Card className="h-full border-border shadow-sm rounded-lg bg-primary-500 text-primary-foreground overflow-hidden">
              <CardContent className="p-8 md:p-10 flex flex-col h-full">
                <div className="size-12 rounded-md bg-white/10 flex items-center justify-center mb-6">
                  <HeartHandshake className="size-6 text-secondary-100" />
                </div>

                <h3 className="text-2xl font-default font-bold mb-5">
                  The right fit matters.
                </h3>

                <p className="text-primary-foreground/85 font-default leading-relaxed mb-6">
                We are always interested in hearing from people who bring good judgment,
                practical skills, respectful communication, and steady follow-through.
                Sometimes that means joining RiverBend directly. Other times, it may mean
                connecting strong candidates with clients who need support.
              </p>

                <p className="text-primary-foreground/85 font-default leading-relaxed mt-auto">
                  Skills we often need include accounting, bookkeeping, program
                  management, project management, management consulting, year-end
                  preparation, Microsoft 365 development, reporting systems, and
                  economic development support.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {pathways.map((item, index) => {
            const Icon = item.icon;
            return <div key={item.title} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
              transitionDelay: `${(index + 2) * 100}ms`
            }}>
                  <Card className="h-full border-border shadow-sm hover:shadow-md transition-shadow rounded-lg bg-card">
                    <CardContent className="p-8 md:p-10 flex flex-col h-full">
                      <div className="size-12 rounded-md bg-secondary/10 flex items-center justify-center mb-6">
                        <Icon className="size-6 text-secondary" />
                      </div>

                      <h3 className="text-2xl font-default font-bold text-primary-500 mb-4">
                        {item.title}
                      </h3>

                      <p className="text-muted-foreground font-default leading-relaxed mb-8">
                        {item.description}
                      </p>

                      <div className="mt-auto">
                        <Button asChild size="lg" className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-white font-default rounded-md">
                          <Link to={item.link}>
                            {item.buttonText}
                            <ArrowRight className="ml-2 size-4" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}